import React, { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { Item } from '@/stores/canvasStore';
import { cn } from '@/lib/utils';
import { acquirePdf, pdfPageAspect, thumbnailWidth } from '@/lib/pdfjs';
import {
  DEFAULT_PAGE_ASPECT,
  dotWindow,
  pdfPagePlacement,
  pdfStackLayout,
  PDF_STACK,
  type StackBox,
} from '@/lib/pdfStack';
import { PaperSkeleton, PdfThumbnail } from './PdfThumbnail';
import { PdfReaderDialog } from './PdfReader';

// PDF card, after the Instagram carousel stack (SocialMediaCard): every page
// as a stack, the current one in front, the next one peeking out behind it,
// dots underneath. Arrows on hover, the dots and the peeking page move
// through the pages. Clicking the front page opens the reader on that page.

interface PdfCardProps {
  item: Item;
  /** Bumped by the canvas on a double-click: the press is a canvas drag, so no click reaches the card. */
  openRequest?: number;
}

interface PdfContent {
  url?: string;
  filename?: string;
  mimeType?: string;
  size?: number;
  storagePath?: string;
  pageCount?: number;
}

/** Filename + meta row under the stack. */
const FOOTER_HEIGHT = 40;
/** A press that moves further than this is a drag, not a click. */
const CLICK_SLOP = 5;

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/** Keeps a press on a stack control from starting a canvas drag, selection or open. */
const stopPress = {
  onMouseDown: (e: React.SyntheticEvent) => e.stopPropagation(),
  onPointerDown: (e: React.SyntheticEvent) => e.stopPropagation(),
  onTouchStart: (e: React.SyntheticEvent) => e.stopPropagation(),
  onDoubleClick: (e: React.SyntheticEvent) => e.stopPropagation(),
};

const px = (b: StackBox): React.CSSProperties => ({ left: b.left, top: b.top, width: b.width, height: b.height });

const SLIDE_MOTION = 'transition-[left,top,width,height,opacity] duration-300 ease-out motion-reduce:transition-none';

export function PdfCard({ item, openRequest = 0 }: PdfCardProps) {
  const content = item.content as PdfContent;
  const { url, filename, size } = content;
  const displayName = filename || 'Document.pdf';
  const displaySize = size ? formatFileSize(size) : '';

  const rootRef = useRef<HTMLDivElement>(null);
  const pressRef = useRef<{ x: number; y: number } | null>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(content.pageCount ?? 0);
  const [aspect, setAspect] = useState(DEFAULT_PAGE_ASPECT);
  const [failed, setFailed] = useState(false);
  const [index, setIndex] = useState(0);
  const [readerOpen, setReaderOpen] = useState(false);

  // Only a request made after mount opens it, so a remount doesn't reopen the reader
  const seenOpenRequest = useRef(openRequest);
  useEffect(() => {
    if (openRequest === seenOpenRequest.current) return;
    seenOpenRequest.current = openRequest;
    setReaderOpen(true);
  }, [openRequest]);

  // Load pdf.js and the document only once the card is near the screen
  useEffect(() => {
    const el = rootRef.current;
    if (!el || isNearViewport) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsNearViewport(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsNearViewport(true); },
      { rootMargin: '400px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isNearViewport]);

  useEffect(() => {
    if (!url || !isNearViewport) return;
    let cancelled = false;
    const { promise, release } = acquirePdf(url);
    setFailed(false);
    promise
      .then(async (pdf) => {
        const firstAspect = await pdfPageAspect(pdf, 1);
        if (cancelled) return;
        setAspect(firstAspect);
        setPageCount(pdf.numPages);
        setDoc(pdf);
      })
      .catch((err) => {
        console.error('[PdfCard] Failed to load PDF', err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
      setDoc(null);
      release();
    };
  }, [url, isNearViewport]);

  const last = Math.max(0, pageCount - 1);
  const current = Math.min(index, last);
  const multi = !!doc && pageCount > 1;
  const flipped = multi && current === last;

  const mediaW = Math.max(0, item.width);
  const mediaH = Math.max(0, item.height - FOOTER_HEIGHT);
  const layout = pdfStackLayout(mediaW, mediaH, aspect, multi);
  const front = flipped ? layout.frontFlipped : layout.front;
  const renderWidth = thumbnailWidth(layout.front.width);

  const go = (to: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIndex(Math.max(0, Math.min(last, to)));
  };

  const handleDownload = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  // The whole card stays draggable; only a press that didn't move opens the reader
  const handlePressStart = (e: React.PointerEvent) => {
    pressRef.current = { x: e.clientX, y: e.clientY };
  };
  const handleOpen = (e: React.MouseEvent) => {
    const press = pressRef.current;
    pressRef.current = null;
    if (e.shiftKey || e.metaKey || e.ctrlKey) return;
    if (press && Math.hypot(e.clientX - press.x, e.clientY - press.y) > CLICK_SLOP) return;
    e.stopPropagation();
    setReaderOpen(true);
  };

  const control = 'pointer-events-auto opacity-0 group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity';
  const dots = multi ? dotWindow(pageCount, current) : [];

  return (
    <>
      <div
        ref={rootRef}
        className="w-full h-full flex flex-col rounded-lg overflow-hidden card-shadow bg-card group"
        data-testid="pdf-card"
      >
        <div
          className="relative flex-1 min-h-0 overflow-hidden bg-muted/70 cursor-pointer"
          onPointerDown={handlePressStart}
          onClick={handleOpen}
          data-testid="pdf-stack"
          role="group"
          aria-roledescription="carousel"
          aria-label={pageCount ? `${displayName}, ${pageCount} pages` : displayName}
        >
          {doc && !failed ? (
            Array.from({ length: pageCount }, (_, i) => {
              const offset = i - current;
              // Only pages one step away can move into view; the rest stay unrendered
              if (Math.abs(offset) > 2) return null;
              const { box, z, visible } = pdfPagePlacement(layout, offset, flipped);
              const isPeek = visible && offset !== 0;
              return (
                <div
                  key={i}
                  className={cn(
                    'absolute overflow-hidden rounded-[5px] bg-white ring-1 ring-black/5',
                    SLIDE_MOTION,
                    offset === 0 ? 'shadow-[0_10px_28px_-8px_rgba(0,0,0,0.4)]' : 'shadow-[0_6px_18px_-8px_rgba(0,0,0,0.3)]',
                  )}
                  style={{ ...px(box), zIndex: z, opacity: visible ? 1 : 0 }}
                  aria-hidden={offset !== 0 && !isPeek}
                  data-testid={offset === 0 ? 'pdf-front' : isPeek ? 'pdf-peek' : undefined}
                  {...(isPeek ? { ...stopPress, onClick: go(i), role: 'button', 'aria-label': `Show page ${i + 1}` } : {})}
                >
                  <PdfThumbnail
                    url={url!}
                    doc={doc}
                    pageNumber={i + 1}
                    renderWidth={renderWidth}
                    alt={offset === 0 ? `${displayName}, page ${i + 1} of ${pageCount}` : ''}
                  />
                </div>
              );
            })
          ) : (
            <div
              className="absolute overflow-hidden rounded-[5px] bg-white ring-1 ring-black/5 shadow-[0_10px_28px_-8px_rgba(0,0,0,0.4)]"
              style={px(layout.front)}
              data-testid="pdf-placeholder"
            >
              <PaperSkeleton pulse={!failed && !!url} />
            </div>
          )}

          {/* Over the front page: counter and arrows. Clicks elsewhere fall through to open the reader. */}
          {multi && (
            <div className={cn('absolute z-[4] pointer-events-none rounded-[5px] overflow-hidden', SLIDE_MOTION)} style={px(front)}>
              <span
                className="absolute top-2 right-2 rounded-full bg-black/55 backdrop-blur-sm px-2 py-0.5 text-[11px] font-medium leading-4 text-white tabular-nums"
                data-testid="pdf-counter"
              >
                {current + 1}/{pageCount}
              </span>
              {current > 0 && (
                <button
                  type="button"
                  className={cn('absolute left-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 text-neutral-900 shadow-md flex items-center justify-center hover:bg-white', control)}
                  aria-label="Previous page"
                  {...stopPress}
                  onClick={go(current - 1)}
                >
                  <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
                </button>
              )}
              {current < last && (
                <button
                  type="button"
                  className={cn('absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/90 text-neutral-900 shadow-md flex items-center justify-center hover:bg-white', control)}
                  aria-label="Next page"
                  {...stopPress}
                  onClick={go(current + 1)}
                >
                  <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
                </button>
              )}
            </div>
          )}

          {/* Dots, centred under the front page; long documents get a sliding window */}
          {multi && (
            <div
              className={cn('absolute z-[4] flex items-center justify-center', SLIDE_MOTION)}
              style={{ left: front.left, top: layout.dotsTop, width: front.width, height: PDF_STACK.dotsHeight }}
            >
              {dots.map((i, n) => {
                const edge = pageCount > dots.length && ((n === 0 && i > 0) || (n === dots.length - 1 && i < last));
                return (
                  <button
                    key={i}
                    type="button"
                    className="p-[3px]"
                    aria-label={`Page ${i + 1}`}
                    aria-current={i === current ? 'true' : undefined}
                    {...stopPress}
                    onClick={go(i)}
                  >
                    <span
                      className={cn(
                        'block rounded-full transition-all duration-300',
                        i === current ? 'h-1.5 w-4 bg-foreground/80' : 'h-1.5 w-1.5 bg-foreground/25 hover:bg-foreground/45',
                        edge && 'scale-[0.6]',
                      )}
                    />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Filename and meta */}
        <div className="shrink-0 flex items-center gap-2 px-3 border-t border-border/50" style={{ height: FOOTER_HEIGHT }}>
          <span className="shrink-0 rounded-[4px] bg-red-500 text-white text-[9px] font-bold leading-none px-1.5 py-1 tracking-wider">
            PDF
          </span>
          <span className="flex-1 min-w-0 truncate text-xs font-medium text-foreground" data-testid="pdf-filename">
            {displayName}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums whitespace-nowrap group-hover:hidden">
            {[pageCount > 0 ? `${pageCount} ${pageCount === 1 ? 'page' : 'pages'}` : null, displaySize || null].filter(Boolean).join(' · ')}
          </span>
          {url && (
            <button
              type="button"
              className="shrink-0 hidden group-hover:flex [@media(hover:none)]:flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Download PDF"
              {...stopPress}
              onClick={handleDownload}
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {url && (
        <PdfReaderDialog
          open={readerOpen}
          onOpenChange={setReaderOpen}
          url={url}
          title={displayName}
          subtitle={displaySize}
          initialPage={current + 1}
          onPageChange={(page) => setIndex(page - 1)}
        />
      )}
    </>
  );
}
